import { formatMoney, round2 } from './money';

export type BidPhaseDefinition = {
  id: number;
  name: string;
  sortOrder: number;
};

export type BidPhaseRollup = {
  material: number;
  labor: number;
  cost: number;
  items: number;
  priced: number;
};

export type BidBreakdownLine = {
  itemId: string;
  phaseId?: string;
  phaseName?: string;
  subPhaseName?: string | null;
  kind: 'material' | 'labor';
  qty: number;
  baseQty: number | null;
  qtyOverride: boolean;
  unitPrice: number;
  supplierPrice: number | null;
  priceOverride: boolean;
  extended: number;
  source: string;
  payableTaskGroup: string | null;
  lineId?: number | null;
  sellPriceKey?: string | null;
  sellPriceOverride?: number | null;
};

export type BidMaterialDetailScope =
  | { kind: 'phase'; phaseId: string; phaseName: string; costKind: 'material' | 'labor' }
  | { kind: 'total' };

export type BidUnpricedLine = {
  modelId: number;
  modelName: string;
  itemId: string;
  phaseId?: string;
  phaseName?: string;
  qty: number | null;
  baseQty: number | null;
  qtyOverride: boolean;
  payableTaskGroup: string | null;
  reason: string;
  source: string;
  lineId: number | null;
};

export type BidTotals = {
  material: number;
  bid: number;
  priced: number;
  items: number;
  phaseCosts?: Record<string, number>;
  uniqueItemCount?: number;
  uniqueUnpricedItemCount?: number;
};

export type PhaseQtyPriceMeta = {
  itemName: string;
  qty: number;
  unitPrice: number;
  matched: boolean;
  missingMap?: boolean;
  missingFloorType?: boolean;
};

export type BidModelRow = {
  id: number;
  name: string;
  floorTypeId?: number | null;
  floorTypeName?: string | null;
  lineCount: number;
  totalPriced: number;
  materialLines?: BidBreakdownLine[];
  laborLines?: BidBreakdownLine[];
  otherCost?: number;
  phases: Record<string, BidPhaseRollup>;
  /** Precomputed labor/material bids per phase when line sell overrides apply (from server). */
  phaseLaborBids?: Record<string, number>;
  phaseLaborSellOverrideFlags?: Record<string, boolean>;
  phaseMaterialBids?: Record<string, number>;
  phaseMaterialSellOverrideFlags?: Record<string, boolean>;
  /** phaseId → price per qty; key present enables qty-price mode. */
  phaseQtyPrices?: Record<string, number>;
  phaseQtyPriceMeta?: Record<string, PhaseQtyPriceMeta>;
  /** Catalog flat phase prices (phaseId → $). */
  divisionPhasePrices?: Record<string, number>;
  /** Community activation (phaseId → 1 when Division Pricing is on). */
  divisionPhaseActive?: Record<string, number>;
  lineSellPrices?: Record<string, number>;
  salesTax: number;
  overrides?: Record<string, number | null> & { lockedPrice?: number | null };
  materialCost: number;
  bidPrice: number;
};

export type BidPayload = {
  phaseDefinitions?: BidPhaseDefinition[];
  margins: Record<string, number>;
  salesTaxPct: number;
  models: BidModelRow[];
  totals?: BidTotals;
  unpricedLines?: BidUnpricedLine[];
};

/** Community default margins — keys like `1:mat`, `1:lab`, plus salesTaxPct */
export type LiveMargins = Record<string, number> & { salesTaxPct: number };

/** Per-model live overrides — margin keys `phaseId:mat` / `phaseId:lab`, plus lockedPrice / phaseQtyPrices */
export type LiveModelOverrideRow = {
  lockedPrice?: string;
  /** Live price-per-qty by phase id; empty string clears mode for that phase. */
  phaseQtyPrices?: Record<string, string>;
  /** Live division activation; '1' = on, '' = off for that phase. */
  divisionPhaseActive?: Record<string, string>;
  /** Margin % drafts (`phaseId:mat` / `phaseId:lab`) and other string fields. */
  [key: string]: string | Record<string, string> | undefined;
};

export type LiveModelOverrides = Record<number, LiveModelOverrideRow>;

export type ModelBidSummary = {
  model: BidModelRow;
  materialTotal: number;
  laborTotal: number;
  materialBidTotal: number;
  laborBidTotal: number;
  totalCost: number;
  totalBid: number;
  profit: number;
  gpPct: string;
  hasLocked: boolean;
  effMaterialMargins: Record<string, number>;
  effLaborMargins: Record<string, number>;
  phaseMaterial: Record<string, number>;
  phaseLabor: Record<string, number>;
  phaseMaterialBid: Record<string, number>;
  phaseLaborBid: Record<string, number>;
  /** True when the phase has at least one labor line sell-price override. */
  phaseLaborHasSellOverrides: Record<string, boolean>;
  /** True when the phase has at least one material line sell-price override. */
  phaseMaterialHasSellOverrides: Record<string, boolean>;
  /** True when the phase is in price-per-qty mode. */
  phaseQtyPriceActive: Record<string, boolean>;
  /** Resolved live price-per-qty (null when mode off). */
  phaseQtyPriceValues: Record<string, number | null>;
  /** True when Division Pricing is active for the phase. */
  divisionPhaseActive: Record<string, boolean>;
  /** Catalog division price when available (null when unset). */
  divisionPhasePriceValues: Record<string, number | null>;
};

/** Build sell-price map key matching the API (`mto` id, or namespaced manual/custom). */
export function sellPriceKeyForLine(line: {
  source?: string;
  lineId?: number | null;
  sellPriceKey?: string | null;
}): string | null {
  if (line.sellPriceKey) return line.sellPriceKey;
  if (line.lineId == null) return null;
  const id = String(line.lineId);
  if (line.source === 'manual_mto') return `manual:${id}`;
  if (line.source === 'custom_labor') return `custom:${id}`;
  return id;
}

export type BidTableRowKind =
  | 'modelHeader'
  | 'materials'
  | 'materialsMargin'
  | 'materialsBid'
  | 'labor'
  | 'laborMargin'
  | 'laborBid';

export type BidTableRow = {
  id: string;
  modelId: number;
  rowKind: BidTableRowKind;
  label: string;
  showLock: boolean;
  summary: ModelBidSummary;
  phaseAmounts: Record<string, number>;
};

export function marginMatKey(phaseId: string | number): string {
  return `${phaseId}:mat`;
}

export function marginLabKey(phaseId: string | number): string {
  return `${phaseId}:lab`;
}

/**
 * Material bid: markup on cost.
 * bid = cost + round2(cost × marginPct / 100)
 */
export function phaseBidAmount(cost: number, marginPct: number): number {
  return round2(cost + round2((cost * marginPct) / 100));
}

/**
 * Labor bid: margin on sell (gross margin).
 * bid = round2(cost / (1 − marginPct/100))
 * e.g. $100 cost @ 20% → $125
 */
export function phaseLaborBidAmount(cost: number, marginPct: number): number {
  if (!(cost > 0) || !(marginPct > 0)) return round2(cost);
  if (marginPct >= 100) return round2(cost);
  return round2(cost / (1 - marginPct / 100));
}

/**
 * Price-per-qty phase split: fixed phase total, labor = (unit×qty) + markup margin,
 * residual to materials.
 *
 * Labor margin here is applied as markup-on-cost (add margin %), matching
 * “labor price × qty, then add the margin” — not the normal labor gross-margin formula.
 */
export function computePhaseQtyPriceSplit(opts: {
  pricePerQty: number;
  qty: number;
  unitPrice: number;
  laborMarginPct: number;
}): {
  laborCost: number;
  laborBid: number;
  materialBid: number;
  materialCost: number;
  phaseTotal: number;
} {
  const qty = Number(opts.qty) || 0;
  const unitPrice = Number(opts.unitPrice) || 0;
  const pricePerQty = Number(opts.pricePerQty) || 0;
  const laborCost = round2(unitPrice * qty);
  const laborBid = phaseBidAmount(laborCost, opts.laborMarginPct);
  const phaseTotal = round2(pricePerQty * qty);
  const materialBid = round2(phaseTotal - laborBid);
  return {
    laborCost,
    laborBid,
    materialBid,
    materialCost: materialBid,
    phaseTotal,
  };
}

/**
 * Flat division phase bid: split total across material/labor by cost ratio.
 * If costs are zero, assign the full total to materials.
 */
export function computeDivisionPhasePriceSplit(opts: {
  phaseTotal: number;
  materialCost: number;
  laborCost: number;
}): { materialBid: number; laborBid: number; phaseTotal: number } {
  const total = round2(Number(opts.phaseTotal) || 0);
  const matCost = Number(opts.materialCost) || 0;
  const labCost = Number(opts.laborCost) || 0;
  const costSum = matCost + labCost;
  if (!(costSum > 0)) {
    return { materialBid: total, laborBid: 0, phaseTotal: total };
  }
  const materialBid = round2(total * (matCost / costSum));
  const laborBid = round2(total - materialBid);
  return { materialBid, laborBid, phaseTotal: total };
}

/** Resolve whether Division Pricing is active for a phase (live overrides win). */
export function resolveDivisionPhaseActive(
  phaseId: string,
  model: BidModelRow,
  liveOv: LiveModelOverrideRow | undefined
): boolean {
  const liveMap = liveOv?.divisionPhaseActive;
  if (liveMap && Object.prototype.hasOwnProperty.call(liveMap, phaseId)) {
    const raw = liveMap[phaseId];
    return raw !== '' && raw != null && raw !== '0';
  }
  return model.divisionPhaseActive?.[phaseId] != null;
}

function liveMarginString(
  liveOv: LiveModelOverrideRow | undefined,
  key: string
): string | undefined {
  if (!liveOv) return undefined;
  const raw = liveOv[key];
  return typeof raw === 'string' ? raw : undefined;
}

/** Resolve live vs saved price-per-qty for a phase (undefined = mode off). */
export function resolvePhaseQtyPrice(
  phaseId: string,
  model: BidModelRow,
  liveOv: LiveModelOverrideRow | undefined
): number | null {
  const liveMap = liveOv?.phaseQtyPrices;
  if (liveMap && Object.prototype.hasOwnProperty.call(liveMap, phaseId)) {
    const raw = liveMap[phaseId];
    if (raw === '' || raw == null) return null;
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) ? round2(n) : null;
  }
  const saved = model.phaseQtyPrices?.[phaseId];
  if (saved == null || saved === ('' as unknown as number)) return null;
  const n = Number(saved);
  return Number.isFinite(n) ? round2(n) : null;
}

/** Labor margin dollars = bid − cost (gross-margin formula). */
export function phaseLaborMarginDollars(cost: number, marginPct: number): number {
  return round2(phaseLaborBidAmount(cost, marginPct) - round2(cost));
}

/** Apply sales tax to a pre-tax material cost (labor is never taxed). */
export function costAfterSalesTax(cost: number, salesTaxPct: number): number {
  if (!(salesTaxPct > 0) || !(cost > 0)) return round2(cost);
  const tax = round2((cost * salesTaxPct) / 100);
  return round2(cost + tax);
}

/** Material: reverse of markup-on-cost (bid = cost × (1 + pct/100)). */
export function marginPctFromBid(cost: number, bid: number): number {
  if (cost <= 0) return 0;
  return Math.round(((bid / cost - 1) * 100 + Number.EPSILON) * 10000) / 10000;
}

/** Labor: reverse of margin-on-sell (bid = cost / (1 − pct/100)). */
export function laborMarginPctFromBid(cost: number, bid: number): number {
  if (!(bid > 0)) return 0;
  return Math.round((((bid - cost) / bid) * 100 + Number.EPSILON) * 10000) / 10000;
}

/** Derive margin % from an edited phase bid amount (cost is fixed). */
export function applyPhaseBidEdit(opts: {
  summary: ModelBidSummary;
  phaseKey: string;
  kind: 'material' | 'labor';
  newBid: number;
}): {
  modelId: number;
  marginKey: string;
  marginValue: string;
} {
  const { summary, phaseKey, kind, newBid } = opts;
  // phaseMaterial is after-tax; phaseLabor is untaxed.
  const cost =
    kind === 'material' ? (summary.phaseMaterial[phaseKey] ?? 0) : (summary.phaseLabor[phaseKey] ?? 0);
  const marginKey = kind === 'material' ? marginMatKey(phaseKey) : marginLabKey(phaseKey);
  const roundedBid = round2(newBid);
  const newMarginPct =
    kind === 'material' ? marginPctFromBid(cost, roundedBid) : laborMarginPctFromBid(cost, roundedBid);

  return {
    modelId: summary.model.id,
    marginKey,
    marginValue: String(newMarginPct),
  };
}

function resolveMarginPct(
  phaseId: string,
  kind: 'mat' | 'lab',
  live: LiveMargins,
  bidMargins: Record<string, number>,
  ov: Record<string, number | null | undefined>,
  liveOv: LiveModelOverrideRow | undefined
): number {
  const key = kind === 'mat' ? marginMatKey(phaseId) : marginLabKey(phaseId);
  const legacy = liveMarginString(liveOv, phaseId);
  const liveVal = liveMarginString(liveOv, key);
  const communityDefault =
    live[key] ?? bidMargins[key] ?? live[phaseId] ?? bidMargins[phaseId] ?? 0;

  if (liveVal !== undefined) {
    return liveVal !== '' ? Number.parseFloat(liveVal) : communityDefault;
  }
  if (legacy !== undefined && legacy !== '') {
    return Number.parseFloat(legacy);
  }
  if (ov[key] != null) return Number(ov[key]);
  if (ov[phaseId] != null) return Number(ov[phaseId] as number);
  return communityDefault;
}

function buildModelSummary(
  m: BidModelRow,
  phaseDefs: BidPhaseDefinition[],
  live: LiveMargins,
  bidMargins: Record<string, number>,
  overrides: LiveModelOverrides
): ModelBidSummary {
  const phaseMaterial: Record<string, number> = {};
  const phaseLabor: Record<string, number> = {};
  const phaseMaterialBid: Record<string, number> = {};
  const phaseLaborBid: Record<string, number> = {};

  const ov = m.overrides || {};
  const liveOv = overrides[m.id] || {};
  const salesTaxPct = live.salesTaxPct;

  const effMaterialMargins: Record<string, number> = {};
  const effLaborMargins: Record<string, number> = {};

  const phaseLaborHasSellOverrides: Record<string, boolean> = {};
  const phaseMaterialHasSellOverrides: Record<string, boolean> = {};
  const phaseQtyPriceActive: Record<string, boolean> = {};
  const phaseQtyPriceValues: Record<string, number | null> = {};
  const divisionPhaseActiveFlags: Record<string, boolean> = {};
  const divisionPhasePriceValues: Record<string, number | null> = {};

  for (const def of phaseDefs) {
    const key = String(def.id);
    const materialPreTax = m.phases[key]?.material ?? 0;
    phaseLabor[key] = m.phases[key]?.labor ?? 0;
    effMaterialMargins[key] = resolveMarginPct(key, 'mat', live, bidMargins, ov, liveOv);
    effLaborMargins[key] = resolveMarginPct(key, 'lab', live, bidMargins, ov, liveOv);

    const catalogDivisionPrice = m.divisionPhasePrices?.[key];
    divisionPhasePriceValues[key] =
      catalogDivisionPrice != null && Number.isFinite(Number(catalogDivisionPrice))
        ? round2(Number(catalogDivisionPrice))
        : null;
    const divisionOn =
      resolveDivisionPhaseActive(key, m, liveOv) && divisionPhasePriceValues[key] != null;
    divisionPhaseActiveFlags[key] = divisionOn;

    const qtyPrice = resolvePhaseQtyPrice(key, m, liveOv);
    // Division blocks Price/Qty; ignore qty mode when division is on.
    phaseQtyPriceValues[key] = divisionOn ? null : qtyPrice;
    phaseQtyPriceActive[key] = !divisionOn && qtyPrice != null;

    if (divisionOn) {
      // Division replaces markup: materials/labor cost and bid cells are zeroed.
      // Phase header / model total bid use the catalog division price instead.
      phaseMaterial[key] = 0;
      phaseLabor[key] = 0;
      phaseMaterialBid[key] = 0;
      phaseLaborBid[key] = 0;
      phaseLaborHasSellOverrides[key] = false;
      phaseMaterialHasSellOverrides[key] = false;
      continue;
    }

    if (qtyPrice != null) {
      const meta = m.phaseQtyPriceMeta?.[key];
      const labPct = effLaborMargins[key]!;
      if (meta?.matched) {
        const split = computePhaseQtyPriceSplit({
          pricePerQty: qtyPrice,
          qty: meta.qty,
          unitPrice: meta.unitPrice,
          laborMarginPct: labPct,
        });
        phaseLabor[key] = split.laborCost;
        phaseLaborBid[key] = split.laborBid;
        phaseMaterial[key] = split.materialCost;
        phaseMaterialBid[key] = split.materialBid;
      } else {
        // Mode on but invalid mapping / missing line — do not fall back to normal pricing.
        phaseLabor[key] = 0;
        phaseLaborBid[key] = 0;
        phaseMaterial[key] = 0;
        phaseMaterialBid[key] = 0;
      }
      phaseLaborHasSellOverrides[key] = false;
      phaseMaterialHasSellOverrides[key] = false;
      continue;
    }

    const matPct = effMaterialMargins[key]!;
    const matLines = (m.materialLines || []).filter((l) => String(l.phaseId) === key);
    const matLinesHaveSell = matLines.some((l) => l.sellPriceOverride != null);
    const matFlagSell = m.phaseMaterialSellOverrideFlags?.[key] === true;
    // Prefer sum of detail lines so bid table matches the details modal.
    if (matLines.length > 0) {
      let matCost = 0;
      let bid = 0;
      for (const line of matLines) {
        const unitTax =
          salesTaxPct > 0 ? round2((line.unitPrice * salesTaxPct) / 100) : 0;
        const costAfterTax = round2(line.unitPrice + unitTax);
        matCost = round2(matCost + round2(costAfterTax * line.qty));
        if (line.sellPriceOverride != null) {
          bid = round2(bid + round2(line.qty * line.sellPriceOverride));
        } else {
          bid = round2(bid + round2(line.qty * phaseBidAmount(costAfterTax, matPct)));
        }
      }
      phaseMaterial[key] = matCost;
      phaseMaterialBid[key] = bid;
      phaseMaterialHasSellOverrides[key] = matLinesHaveSell;
    } else if (matFlagSell && m.phaseMaterialBids?.[key] != null) {
      phaseMaterial[key] = costAfterSalesTax(materialPreTax, salesTaxPct);
      phaseMaterialBid[key] = round2(m.phaseMaterialBids[key]!);
      phaseMaterialHasSellOverrides[key] = true;
    } else {
      // Fallback when line breakdown is not on the payload.
      phaseMaterial[key] = costAfterSalesTax(materialPreTax, salesTaxPct);
      phaseMaterialBid[key] = phaseBidAmount(phaseMaterial[key]!, matPct);
      phaseMaterialHasSellOverrides[key] = false;
    }

    const labPct = effLaborMargins[key]!;
    const phaseLines = (m.laborLines || []).filter((l) => String(l.phaseId) === key);
    const linesHaveSell = phaseLines.some((l) => l.sellPriceOverride != null);
    const flagSell = m.phaseLaborSellOverrideFlags?.[key] === true;
    if (phaseLines.length > 0) {
      let bid = 0;
      for (const line of phaseLines) {
        if (line.sellPriceOverride != null) {
          bid = round2(bid + round2(line.qty * line.sellPriceOverride));
        } else {
          bid = round2(bid + round2(line.qty * phaseLaborBidAmount(line.unitPrice, labPct)));
        }
      }
      phaseLaborBid[key] = bid;
      phaseLaborHasSellOverrides[key] = linesHaveSell;
    } else if (flagSell && m.phaseLaborBids?.[key] != null) {
      phaseLaborBid[key] = round2(m.phaseLaborBids[key]!);
      phaseLaborHasSellOverrides[key] = true;
    } else {
      phaseLaborBid[key] = phaseLaborBidAmount(phaseLabor[key]!, labPct);
      phaseLaborHasSellOverrides[key] = false;
    }
  }

  const materialTotal = round2(Object.values(phaseMaterial).reduce((s, v) => s + v, 0));
  const laborTotal = round2(Object.values(phaseLabor).reduce((s, v) => s + v, 0));
  const totalCost = round2(materialTotal + laborTotal);

  let materialMarginDollars = 0;
  let laborMarginDollars = 0;
  let divisionBidTotal = 0;
  for (const def of phaseDefs) {
    const key = String(def.id);
    if (divisionPhaseActiveFlags[key]) {
      divisionBidTotal = round2(divisionBidTotal + (divisionPhasePriceValues[key] ?? 0));
      continue;
    }
    materialMarginDollars += round2(phaseMaterialBid[key]! - phaseMaterial[key]!);
    laborMarginDollars += round2(phaseLaborBid[key]! - phaseLabor[key]!);
  }
  const materialBidTotal = round2(materialTotal + materialMarginDollars);
  const laborBidTotal = round2(laborTotal + laborMarginDollars);

  const savedLocked =
    ov.lockedPrice != null && Number(ov.lockedPrice) > 0 ? String(ov.lockedPrice) : '';
  const liveLockedVal = liveMarginString(liveOv, 'lockedPrice');
  const lockedRaw =
    liveLockedVal !== undefined ? liveLockedVal : savedLocked !== '' ? savedLocked : '';
  const lockedNum = lockedRaw !== '' ? Number.parseFloat(lockedRaw) : Number.NaN;
  const hasLocked = !Number.isNaN(lockedNum) && lockedNum > 0;

  let computedBid = totalCost;
  if (!hasLocked) {
    computedBid = round2(totalCost + materialMarginDollars + laborMarginDollars + divisionBidTotal);
  }

  const totalBid = hasLocked ? lockedNum : round2(computedBid);
  const profit = round2(totalBid - totalCost);

  return {
    model: m,
    materialTotal,
    laborTotal,
    materialBidTotal,
    laborBidTotal,
    totalCost,
    totalBid: round2(totalBid),
    profit,
    gpPct: totalBid > 0 ? ((profit / totalBid) * 100).toFixed(1) : '—',
    hasLocked,
    effMaterialMargins,
    effLaborMargins,
    phaseMaterial,
    phaseLabor,
    phaseMaterialBid,
    phaseLaborBid,
    phaseLaborHasSellOverrides,
    phaseMaterialHasSellOverrides,
    phaseQtyPriceActive,
    phaseQtyPriceValues,
    divisionPhaseActive: divisionPhaseActiveFlags,
    divisionPhasePriceValues,
  };
}

/** Per-floor-type (or total) rollup for the bid builder summary. */
export type BidLotStoryStats = {
  key: string;
  label: string;
  /** Models in this floor-type bucket (used for averages). */
  modelCount: number;
  avgCost: number;
  avgPrice: number;
  profit: number;
  gpPct: string;
};

function gpPctLabel(profit: number, totalBid: number): string {
  return totalBid > 0 ? ((profit / totalBid) * 100).toFixed(1) : '—';
}

function buildLotStatsBucket(
  key: string,
  label: string,
  modelCount: number,
  totalCost: number,
  totalBid: number
): BidLotStoryStats {
  const profit = round2(totalBid - totalCost);
  return {
    key,
    label,
    modelCount,
    avgCost: modelCount ? round2(totalCost / modelCount) : 0,
    avgPrice: modelCount ? round2(totalBid / modelCount) : 0,
    profit,
    gpPct: gpPctLabel(profit, totalBid),
  };
}

/** Classify a model's floor type into 1 Story / 2 Story / Other. */
export function storyBucketKey(floorTypeName: string | null | undefined): '1' | '2' | 'other' {
  const name = (floorTypeName ?? '').trim().toLowerCase();
  if (/^1(\s|-)?stor(y|ies)\b/.test(name) || name === '1') return '1';
  if (/^2(\s|-)?stor(y|ies)\b/.test(name) || name === '2') return '2';
  return 'other';
}

export function buildLotStoryStats(summaries: ModelBidSummary[]): {
  byStory: BidLotStoryStats[];
  total: BidLotStoryStats;
} {
  let oneCost = 0;
  let oneBid = 0;
  let oneModels = 0;
  let twoCost = 0;
  let twoBid = 0;
  let twoModels = 0;
  let otherCost = 0;
  let otherBid = 0;
  let otherModels = 0;
  let totalCost = 0;
  let totalBid = 0;

  for (const summary of summaries) {
    const cost = summary.totalCost;
    const bid = summary.totalBid;
    totalCost += cost;
    totalBid += bid;
    const bucket = storyBucketKey(summary.model.floorTypeName);
    if (bucket === '1') {
      oneCost += cost;
      oneBid += bid;
      oneModels += 1;
    } else if (bucket === '2') {
      twoCost += cost;
      twoBid += bid;
      twoModels += 1;
    } else {
      otherCost += cost;
      otherBid += bid;
      otherModels += 1;
    }
  }

  const byStory: BidLotStoryStats[] = [
    buildLotStatsBucket('1', '1 Story', oneModels, oneCost, round2(oneBid)),
    buildLotStatsBucket('2', '2 Story', twoModels, twoCost, round2(twoBid)),
  ];
  if (otherModels > 0) {
    byStory.push(buildLotStatsBucket('other', 'Other', otherModels, otherCost, round2(otherBid)));
  }

  return {
    byStory,
    total: buildLotStatsBucket('total', 'Total', summaries.length, totalCost, round2(totalBid)),
  };
}

export function computeBidTable(
  bid: BidPayload,
  live: LiveMargins,
  overrides: LiveModelOverrides
): {
  rows: BidTableRow[];
  summaries: ModelBidSummary[];
  grandTotalCost: number;
  grandBid: number;
  avgCost: number;
  avgBid: number;
  lotStoryStats: ReturnType<typeof buildLotStoryStats>;
  uniqueUnpricedItemCount: number;
  uniqueItemCount: number;
  unpricedLines: BidUnpricedLine[];
  pricingComplete: boolean;
} {
  let grandTotalCost = 0;
  let grandBid = 0;
  const uniqueUnpricedItemCount = bid.totals?.uniqueUnpricedItemCount ?? 0;
  const uniqueItemCount = bid.totals?.uniqueItemCount ?? 0;
  const unpricedLines = bid.unpricedLines ?? [];
  const pricingComplete = uniqueUnpricedItemCount === 0;
  const phaseDefs = bid.phaseDefinitions ?? [];

  const summaries: ModelBidSummary[] = bid.models.map((m) =>
    buildModelSummary(m, phaseDefs, live, bid.margins, overrides)
  );

  const rows: BidTableRow[] = [];
  for (const summary of summaries) {
    const mid = summary.model.id;
    const phaseBidAmounts: Record<string, number> = {};
    for (const def of phaseDefs) {
      const key = String(def.id);
      if (summary.divisionPhaseActive[key]) {
        phaseBidAmounts[key] = round2(summary.divisionPhasePriceValues[key] ?? 0);
      } else {
        phaseBidAmounts[key] = round2(
          (summary.phaseMaterialBid[key] ?? 0) + (summary.phaseLaborBid[key] ?? 0)
        );
      }
    }
    rows.push({
      id: `${mid}-header`,
      modelId: mid,
      rowKind: 'modelHeader',
      label: summary.model.name,
      showLock: true,
      summary,
      phaseAmounts: phaseBidAmounts,
    });
    rows.push({
      id: `${mid}-materials`,
      modelId: mid,
      rowKind: 'materials',
      label: 'Materials',
      showLock: false,
      summary,
      phaseAmounts: summary.phaseMaterial,
    });
    rows.push({
      id: `${mid}-materials-margin`,
      modelId: mid,
      rowKind: 'materialsMargin',
      label: 'Materials Markup',
      showLock: false,
      summary,
      phaseAmounts: {},
    });
    rows.push({
      id: `${mid}-materials-bid`,
      modelId: mid,
      rowKind: 'materialsBid',
      label: 'Materials Bid',
      showLock: false,
      summary,
      phaseAmounts: summary.phaseMaterialBid,
    });
    rows.push({
      id: `${mid}-labor`,
      modelId: mid,
      rowKind: 'labor',
      label: 'Labor',
      showLock: false,
      summary,
      phaseAmounts: summary.phaseLabor,
    });
    rows.push({
      id: `${mid}-labor-margin`,
      modelId: mid,
      rowKind: 'laborMargin',
      label: 'Labor Margin',
      showLock: false,
      summary,
      phaseAmounts: {},
    });
    rows.push({
      id: `${mid}-labor-bid`,
      modelId: mid,
      rowKind: 'laborBid',
      label: 'Labor Bid',
      showLock: false,
      summary,
      phaseAmounts: summary.phaseLaborBid,
    });
    grandTotalCost += summary.totalCost;
    grandBid += summary.totalBid;
  }

  const modelCount = summaries.length;
  const roundedGrandBid = round2(grandBid);
  const lotStoryStats = buildLotStoryStats(summaries);
  return {
    rows,
    summaries,
    grandTotalCost,
    grandBid: roundedGrandBid,
    avgCost: modelCount ? round2(grandTotalCost / modelCount) : 0,
    avgBid: modelCount ? round2(roundedGrandBid / modelCount) : 0,
    lotStoryStats,
    uniqueUnpricedItemCount,
    uniqueItemCount,
    unpricedLines,
    pricingComplete,
  };
}

/** @deprecated Use computeBidTable */
export type ComputedBidRow = ModelBidSummary;

/** @deprecated Use computeBidTable */
export function computeBidRows(bid: BidPayload, live: LiveMargins, overrides: LiveModelOverrides) {
  const result = computeBidTable(bid, live, overrides);
  return {
    rows: result.summaries,
    grandTotalCost: result.grandTotalCost,
    grandBid: result.grandBid,
    uniqueUnpricedItemCount: result.uniqueUnpricedItemCount,
    uniqueItemCount: result.uniqueItemCount,
    unpricedLines: result.unpricedLines,
    pricingComplete: result.pricingComplete,
  };
}

/** Cost vs price amounts for one phase × model in the qty summary table. */
export type PhaseQtySummaryCell = {
  phaseId: string;
  phaseName: string;
  modelId: number;
  modelName: string;
  qty: number | null;
  /** Mapped MTO item name when matched. */
  itemName: string | null;
  qtyPriceMode: boolean;
  mapped: boolean;
  /** MTO labor extended (unit × qty) when mapped; else phase labor cost. */
  laborCost: number;
  /** Labor with margin markup when mapped; else phase labor bid. */
  laborPrice: number;
  /** Residual of phase total after labor price. */
  materialsCost: number;
  materialsPrice: number;
  /** laborCost + materialsCost */
  totalCost: number;
  /** Phase total: price×qty in qty mode, otherwise phase bid total. */
  totalPrice: number;
};

/**
 * Build phase × model summary cells using qty mappings for every phase
 * (whether or not the phase is currently priced by qty).
 */
export function buildPhaseQtySummary(
  summaries: ModelBidSummary[],
  phaseDefs: BidPhaseDefinition[]
): PhaseQtySummaryCell[] {
  const out: PhaseQtySummaryCell[] = [];
  for (const def of phaseDefs) {
    const phaseId = String(def.id);
    for (const summary of summaries) {
      const meta = summary.model.phaseQtyPriceMeta?.[phaseId];
      const labPct = summary.effLaborMargins[phaseId] ?? 0;
      const qtyActive = !!summary.phaseQtyPriceActive[phaseId];
      const phaseBidTotal = round2(
        (summary.phaseMaterialBid[phaseId] ?? 0) + (summary.phaseLaborBid[phaseId] ?? 0)
      );

      let laborCost = summary.phaseLabor[phaseId] ?? 0;
      let laborPrice = summary.phaseLaborBid[phaseId] ?? 0;
      let totalPrice = phaseBidTotal;
      let qty: number | null = null;
      let itemName: string | null = null;
      let mapped = false;

      if (meta?.matched) {
        mapped = true;
        qty = meta.qty;
        itemName = meta.itemName?.trim() || null;
        laborCost = round2(meta.unitPrice * meta.qty);
        laborPrice = phaseBidAmount(laborCost, labPct);
        const pricePerQty = summary.phaseQtyPriceValues[phaseId];
        if (qtyActive && pricePerQty != null) {
          totalPrice = round2(pricePerQty * meta.qty);
        }
      }

      const materialsPrice = round2(totalPrice - laborPrice);
      const materialsCost = materialsPrice;
      const totalCost = round2(laborCost + materialsCost);

      out.push({
        phaseId,
        phaseName: def.name,
        modelId: summary.model.id,
        modelName: summary.model.name,
        qty,
        itemName,
        qtyPriceMode: qtyActive,
        mapped,
        laborCost,
        laborPrice,
        materialsCost,
        materialsPrice,
        totalCost,
        totalPrice,
      });
    }
  }
  return out;
}

/** Delta of current vs baseline; null when equal within round2. */
export function revisionCompareDelta(current: number, baseline: number): number | null {
  const delta = round2(current - baseline);
  return delta === 0 ? null : delta;
}

/** Signed money delta for compare UI (`+$1.00` / `-$1.00`). Empty when no delta. */
export function formatRevisionCompareDelta(delta: number | null): string {
  if (delta == null) return '';
  const abs = formatMoney(Math.abs(delta));
  return delta > 0 ? `+${abs}` : `-${abs}`;
}

/** Summaries keyed by model id from a frozen revision snapshot payload. */
export function revisionSummaryByModelId(snapshotBid: BidPayload): Map<number, ModelBidSummary> {
  const snapLive: LiveMargins = {
    ...snapshotBid.margins,
    salesTaxPct: snapshotBid.salesTaxPct,
  };
  const { summaries } = computeBidTable(snapshotBid, snapLive, {});
  return new Map(summaries.map((s) => [s.model.id, s]));
}
